import React from 'react';
import { Link, OtpInput } from '@ntuctech/devex-tangram';
import { Button, Text } from '@ntuctech/tangram-web';
import { useRouter } from 'next/router';
import styled from 'styled-components';

import { PWL_CONTACT_EMAIL, PWL_CONTACT_SMS } from '../../../services/api/constants';
import { fetcherApiRoute } from '../../../services/api/fetcher';
import { startPasswordless } from '../../../services/profile';

import { updateMiCallbackUiAfterSave } from './helperFunction';

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 100%;
`;

const SubmitButtonSc = styled(Button)`
  margin-left: 1rem;
`;

const InformationText = styled.div`
  margin-bottom: 2rem;
`;

const commonTrackingParams = {
  screenName: 'account | myinfo onboarding verification otp',
  previousScreenName: 'account | my info onboarding verification',
  previousScreenSection: null,
  buttonType: 'navigational style',
};

const MyinfoOtpInput = ({
  value,
  setToggleView,
  handleCloseSideSheet,
  keyValue,
  myinfoData,
  setMyinfoData,
  setEditableFlowError,
  setShowToast,
  isPersonaEnabled,
  flowType,
  trackSegment,
  getSegmentUtmParams,
  setIsEmailVerified,
  setIsMobileVerified,
  isMyinfoFapiEnabled,
}) => {
  const [otp, setOtp] = React.useState('');
  const [otpError, setOtpError] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const router = useRouter();
  const { query } = router;
  const getUtmParam = getSegmentUtmParams(query);

  const handleOtpValueChange = (e) => {
    setOtpError('');
    setOtp(e);
  };

  const isPreprod = process.env.ENV === 'preprod' || process.env.ENV === 'development';

  const handleCancel = () => {
    if (flowType === 'myinfo') {
      const trackingParams = {
        ...commonTrackingParams,
        screenSection:
          keyValue === 'email'
            ? 'widget | myinfo edit email'
            : 'widget | myinfo edit mobile number',
        buttonName: 'myinfo cancel',
      };
      trackSegment && trackSegment('Button Clicked', trackingParams, getUtmParam);
    }
    setToggleView('input');
  };

  const handleSubmit = () => {
    setLoading(true);
    if (flowType === 'myinfo') {
      const trackingParams = {
        ...commonTrackingParams,
        screenSection:
          keyValue === 'email'
            ? 'widget | myinfo edit email'
            : 'widget | myinfo edit mobile number',
        buttonName: 'myinfo submit',
      };
      trackSegment && trackSegment('Button Clicked', trackingParams, getUtmParam);
      const newSubmitBody = {
        session_id: myinfoData.session_id,
        field: keyValue,
        verification_code: otp,
      };

      if (keyValue === 'email') {
        newSubmitBody.email = value;
      } else {
        newSubmitBody.phone = value.replace(/\s/g, '');
      }
      fetcherApiRoute('/api/v2/myinfo-signup-data', {
        method: 'PATCH',
        body: JSON.stringify(newSubmitBody),
      })
        .then(() => {
          keyValue === 'email' ? setIsEmailVerified(true) : setIsMobileVerified(true);
          updateMiCallbackUiAfterSave({
            myinfoData,
            setMyinfoData,
            newValue: value,
            key: keyValue,
            handleCloseSideSheet,
            setShowToast,
          });
        })
        .catch((err) => {
          if (err.message.includes('verification code')) {
            setOtpError('The OTP you’ve entered is incorrect. Please try again.');
          } else {
            setEditableFlowError(true);
          }
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      const submitBody = {
        value: keyValue === 'email' ? value : value.replace(/\s/g, ''),
        verificationCode: otp,
        field: keyValue,
        sessionId: myinfoData.session_id,
      };
      fetcherApiRoute(
        /* eslint-disable */
        isMyinfoFapiEnabled
          ? '/api/v2/myinfo-personaldetails-fapi'
          : isPersonaEnabled
          ? '/api/v2/myinfo-update-personaldetails'
          : '/api/myinfo-update-personaldetails',
        {
          method: 'POST',
          body: JSON.stringify(submitBody),
        },
      )
        .then(() => {
          updateMiCallbackUiAfterSave({
            myinfoData,
            setMyinfoData,
            newValue: value,
            key: keyValue,
            handleCloseSideSheet,
            setShowToast,
          });
        })
        .catch((err) => {
          if (err.message.includes('verification code')) {
            setOtpError('The OTP you’ve entered is incorrect. Please try again.');
          } else {
            setEditableFlowError(true);
          }
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const resendOtp = () => {
    startPasswordless(
      keyValue === 'email' ? PWL_CONTACT_EMAIL : PWL_CONTACT_SMS,
      keyValue === 'email' ? value : value.replace(/\s/g, ''),
      () => {},
      () => setEditableFlowError(true),
      () => null,
    );
  };

  return (
    <div data-testid="OtpWrapper">
      <InformationText>
        <Text>A 6-digit code has been sent to {value}</Text>
      </InformationText>
      <OtpInput
        id="OtpInput"
        label="Enter 6-digit code"
        isNum
        length={6}
        resendTimer={25}
        onChange={handleOtpValueChange}
        value={otp}
        error={Boolean(otpError)}
        helpText={otpError ?? ''}
        onResend={resendOtp}
      />
      {/* eslint-disable */}
      {isPreprod && keyValue === 'mobileno' && (
        <div style={{ marginTop: '1rem' }}>
          <div>
            <Text bold>For Preprod only:</Text>
          </div>
          <div style={{ marginTop: '1rem' }}>
            <Text>
              Please click{' '}
              <Link
                target="_blank"
                rel="noopener noreferrer"
                href={`https://fxztgmmpm7.execute-api.ap-southeast-1.amazonaws.com/api/record/id/${value.replace(
                  /\s/g,
                  '',
                )}`}
              >
                https://fxztgmmpm7.execute-api.ap-southeast-1.amazonaws.com/api/record/id/
                {value.replace(/\s/g, '')}
              </Link>{' '}
              to get your otp
            </Text>
          </div>
        </div>
      )}
      <ButtonWrapper>
        <Button variant="secondary" onClick={handleCancel}>
          Cancel
        </Button>
        <SubmitButtonSc
          variant="primary"
          onClick={handleSubmit}
          disabled={otp.length < 6}
          loading={loading}
        >
          Submit
        </SubmitButtonSc>
      </ButtonWrapper>
    </div>
  );
};

export default MyinfoOtpInput;
